<template>
  <div class="editor-view-container overflow-hidden">
    <EditorSkeletonLoader
      v-if="pageLoading"
      :side-by-side-view="!!sideBySideView"
    />

    <EditorLayout
      class="h-100 w-100"
      :side-by-side-view="sideBySideView"
    >
      <template
        v-if="authData.accessLevel > 2"
        #full-width-header
      >
        <v-sheet
          max-width="1800px"
          class="mx-auto"
        >
          <SideBySideTopBar v-if="sideBySideView" />
          <TopBar v-else />
        </v-sheet>
      </template>

      <template
        v-if="!pageLoading"
        #side-revision
      >
        <JobProfileSideRevision />
      </template>

      <template
        v-if="!pageLoading"
        #content
      >
        <JobProfileEditorContent
          v-if="authData.accessLevel > 2"
          class="w-100"
        />
        <JobProfileLimitedContent
          v-else
        />
      </template>
      <template
        v-if="authData.accessLevel > 2"
        #left
      >
        <LeftSideBar />
      </template>
      <template
        v-if="authData.accessLevel > 2"
        #right
      >
        <RightSideBar />
      </template>

      <template #content-footer>
        <EditorToolbarContainer
          class="pt-1 pb-1"
          :show-comments="true"
        />
      </template>
    </EditorLayout>
  </div>
</template>

<script setup>
import { onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppRoute } from '@/stores/app/route';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { useSideBySideView } from '@/stores/document/sideBySideView';
import EditorSkeletonLoader from '@/components/shared/SkeletonLoaders/editor/EditorSkeletonLoader.vue';
import EditorLayout from '@/components/editor/EditorLayout.vue';
import JobProfileEditorContent from '@/components/jobProfiles/editor/JobProfileEditorContent.vue';
import JobProfileLimitedContent from '@/components/jobProfiles/editor/JobProfileLimitedContent.vue';
import JobProfileSideRevision from '@/components/jobProfiles/editor/JobProfileSideRevision.vue';
import LeftSideBar from '@/components/jobProfiles/editor/LeftSideBar.vue';
import RightSideBar from '@/components/jobProfiles/editor/RightSideBar.vue';
import TopBar from '@/components/jobProfiles/editor/TopBar.vue';
import SideBySideTopBar from '@/components/jobProfiles/editor/SideBySideTopBar.vue';
import EditorToolbarContainer from '@/components/document/editorToolbar/EditorToolbarContainer.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  isHris: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const { pageLoading } = storeToRefs(useAppLoading());

const { authData } = storeToRefs(useAppAuthData());

const { route } = storeToRefs(useAppRoute());

const { sideBySideView } = storeToRefs(useSideBySideView());

const { summaryFieldId } = storeToRefs(useCurrentJobProfile());

const load = async () => {
  const { loadJobProfile } = useCurrentJobProfile();
  await loadJobProfile({ ulid: props.id, isHris: props.isHris, rescore: true });
};

const loadRevision = (revisionUlid) => {
  if (revisionUlid) {
    useCurrentJobProfile().loadProfileRevision({ revisionUlid });
  }
};

onMounted(async () => {
  await load();
  return loadRevision(route.value?.query?.sideRevision);
});

watch(() => props.id, () => {
  load();
});

watch(pageLoading, (loading) => {
  if (!loading && route.value?.query?.writeWithFirstDraft) {
    router.replace({ query: { ...route.value.query, writeWithFirstDraft: undefined } }).catch(() => {});
    useFirstDraft().generateAndApplyJobProfile({ ulid: props.id, summaryFieldId: summaryFieldId.value });
  }
});

watch(() => route.value?.query?.sideRevision, (revisionUlid) => {
  loadRevision(revisionUlid);
});
</script>
